import React, { useMemo, useState } from 'react';
import { Box, FlexList, Preloader, useResponsive } from '@common-fe/common-fe';

import useDependentsQuery from '@/modules/user/components/ManageProfile/components/Dependents/queries/useDependents.query';
import useHighlightedDependentStore from '@/modules/user/components/ManageProfile/components/Dependents/stores/useHighlightedDependent.store';

import EditDependentModal from '../DependentsModal/EditDependentModal';
import RemoveDependentModal from '../DependentsModal/RemoveDependentModal';

import useDependentsList, { HEADERS } from './hooks/useDependentsList';
import MobileDependentCard from './MobileDependentCard';

const DependentsList = () => {
  const { isMobile } = useResponsive();
  const [isRemoveDependentModalVisible, setIsRemoveDependentModalVisible] = useState(false);
  const [isEditDependentModalVisible, setIsEditDependentModalVisible] = useState(false);
  const { highlightedDependentId } = useHighlightedDependentStore();

  const { formattedData, isLoading } = useDependentsQuery();

  const onDeactivate = () => setIsRemoveDependentModalVisible(true);
  const onEdit = () => setIsEditDependentModalVisible(true);

  const formattedList = useDependentsList({
    dependents: formattedData,
    highlightedDependentId,
    onDeactivate,
    onEdit,
  });

  const sortedDependents = useMemo(
    () =>
      [...(formattedData || [])].sort((prev, next) =>
        (prev?.firstName || '').localeCompare(next?.firstName || '')
      ),
    [formattedData]
  );

  const renderList = () => {
    if (isMobile) {
      return sortedDependents.map((dependent) => (
        <MobileDependentCard
          key={dependent.id}
          dependent={dependent}
          isHighlighted={Boolean(highlightedDependentId && highlightedDependentId === dependent.id)}
          onEdit={onEdit}
          onDeactivate={onDeactivate}
        />
      ));
    }

    return (
      <FlexList
        headers={HEADERS}
        rows={formattedList}
        total={formattedList.length}
        pad="0px"
        testId="DependentsList"
      />
    );
  };

  return (
    <>
      <Box margin={{ top: 'spacing16' }} data-testid="DependentsList_wrapper">
        {isLoading ? (
          <Box pad={{ bottom: 'spacing24' }}>
            <Preloader testId="DependentsList_preloader" />
          </Box>
        ) : (
          renderList()
        )}
      </Box>
      {isRemoveDependentModalVisible && (
        <RemoveDependentModal onSetVisible={setIsRemoveDependentModalVisible} />
      )}
      {isEditDependentModalVisible && <EditDependentModal onSetVisible={setIsEditDependentModalVisible} />}
    </>
  );
};

export default DependentsList;
